import { useState, useEffect } from "react";
import { Plus, Search, Building2, Phone, Mail, MapPin, CreditCard, FileText, ChevronRight, Edit, Trash2, X, Check, AlertCircle } from "lucide-react";
import { suppliersAPI, invoicesAPI } from "../services/api";
import { toast } from "sonner";

const PAYMENT_TERMS = [15, 30, 45, 60, 90];
const normalizeSearchText = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const EMPTY = { name: "", nif: "", contact_name: "", phone: "", email: "", address: "", city: "", postal_code: "", country: "PT", website: "", iban: "", payment_terms: 30, notes: "" };

function Badge({ status }) {
  const map = { paid: ["Pago", "bg-green-900/40 text-green-400"], pending: ["Pendente", "bg-yellow-900/40 text-yellow-400"], overdue: ["Vencido", "bg-red-900/40 text-red-400"], partial: ["Parcial", "bg-blue-900/40 text-blue-400"] };
  const [label, cls] = map[status] || ["Desconhecido", "bg-gray-700 text-gray-400"];
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cls}`}>{label}</span>;
}

export default function SuppliersPage() {
  const [suppliers, setSuppliers] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [balance, setBalance] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showInvoiceDetail, setShowInvoiceDetail] = useState(false);

  useEffect(() => { loadSuppliers(); }, []);

  const loadSuppliers = async () => {
    setLoading(true);
    try {
      const res = await suppliersAPI.getAllFull();
      setSuppliers(res.data || []);
    } catch (err) {
      console.error("Erro ao carregar fornecedores:", err);
    } finally { setLoading(false); }
  };

  const selectSupplier = async (s) => {
    setSelected(s);
    try {
      const [balRes, invRes] = await Promise.all([suppliersAPI.getBalance(s.id), suppliersAPI.getInvoices(s.id)]);
      setBalance(balRes.data);
      setInvoices(invRes.data || []);
    } catch (err) {
      console.error("Erro ao carregar detalhe do fornecedor:", err);
      setBalance(null);
      setInvoices([]);
    }
  };

  const openCreate = () => { setForm(EMPTY); setEditing(null); setShowForm(true); };
  const openEdit = (s) => { setForm({ ...s }); setEditing(s.id); setShowForm(true); };

  const openInvoiceDetail = async (invoice) => {
    if (!invoice) return;

    setSelectedInvoice(invoice);
    setShowInvoiceDetail(true);

    // Ir buscar a fatura completa para garantir que vêm os artigos/linhas.
    if (invoice.id) {
      try {
        const res = await invoicesAPI.getById(invoice.id);
        setSelectedInvoice(res?.data || invoice);
      } catch (err) {
        console.error("Erro ao carregar detalhe completo da fatura:", err);
        setSelectedInvoice(invoice);
      }
    }
  };

  const closeInvoiceDetail = () => {
    setShowInvoiceDetail(false);
    setSelectedInvoice(null);
  };

  const formatMoney = (value) => `${Number(value || 0).toFixed(2)}€`;

  const formatDate = (value) => {
    if (!value) return "-";
    try {
      return new Date(value).toLocaleDateString("pt-PT");
    } catch {
      return value;
    }
  };

  const invoiceOpenAmount = (invoice) => {
    const total = Number(invoice?.total || 0);
    const paid = Number(invoice?.paid_amount || invoice?.amount_paid || 0);
    const open = invoice?.open_amount ?? invoice?.pending_amount ?? invoice?.amount_due;
    if (open !== undefined && open !== null && open !== "") return Number(open || 0);
    return Math.max(0, total - paid);
  };

  const invoiceItems = (invoice) => {
    const raw = invoice?.items || invoice?.lines || invoice?.invoice_items || invoice?.products || [];
    return Array.isArray(raw) ? raw : [];
  };

  const itemName = (item) =>
    item?.product_name ||
    item?.name ||
    item?.description ||
    item?.title ||
    item?.product?.name ||
    "Produto";

  const itemQty = (item) =>
    Number(item?.quantity ?? item?.qty ?? item?.units ?? 1);

  const itemUnitPrice = (item) =>
    Number(item?.unit_price ?? item?.price ?? item?.cost_price ?? item?.unit_cost ?? 0);

  const itemVatRate = (item) =>
    item?.vat_rate ?? item?.tax_rate ?? item?.tax ?? "";

  const itemLineTotal = (item) => {
    const total =
      item?.total ??
      item?.line_total ??
      item?.subtotal ??
      item?.line_subtotal ??
      item?.amount;

    if (total !== undefined && total !== null && total !== "") {
      return Number(total || 0);
    }

    return itemQty(item) * itemUnitPrice(item);
  };

  const saveSupplier = async () => {
    if (!form.name) return;
    setSaving(true);
    try {
      if (editing) { await suppliersAPI.update(editing, form); }
      else { await suppliersAPI.create(form); }
      await loadSuppliers();
      setShowForm(false);
      toast.success("Fornecedor guardado com sucesso");
    } catch (err) {
      console.error("Erro ao guardar fornecedor:", err);
      toast.error(err?.response?.data?.detail || "Não foi possível guardar o fornecedor");
    } finally { setSaving(false); }
  };

  const deleteSupplier = async (id) => {
    if (!window.confirm("Desativar este fornecedor?")) return;
    await suppliersAPI.delete(id);
    setSelected(null);
    await loadSuppliers();
  };

  const filtered = suppliers.filter((s) => {
    if (s.is_active === false) return false;

    const q = normalizeSearchText(search);
    if (!q) return true;

    const searchBlob = normalizeSearchText([
      s?.name,
      s?.contact_name,
      s?.email,
      s?.phone,
      s?.nif,
      s?.address,
      s?.city,
      s?.postal_code,
      s?.website,
      s?.iban,
    ].filter(Boolean).join(' '));

    return searchBlob.includes(q);
  });

  return (
    <div className="flex h-full">
      {/* Lista */}
      <div className="w-80 border-r border-gray-800 flex flex-col">
        <div className="p-4 border-b border-gray-800">
          <div className="flex items-center justify-between mb-3">
            <h1 className="text-lg font-bold text-white">Fornecedores</h1>
            <button onClick={openCreate} className="bg-green-600 hover:bg-green-500 text-white p-1.5 rounded-lg transition-colors">
              <Plus className="w-4 h-4" />
            </button>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Pesquisar..." className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-green-600" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? <p className="text-gray-500 text-sm p-4">A carregar...</p> : filtered.length === 0 ? (
            <p className="text-gray-500 text-sm p-4">Nenhum fornecedor encontrado.</p>
          ) : filtered.map(s => (
            <div key={s.id} onClick={() => selectSupplier(s)} className={`p-3 border-b border-gray-800/50 cursor-pointer hover:bg-gray-800/50 transition-colors ${selected?.id === s.id ? 'bg-gray-800 border-l-2 border-l-green-500' : ''}`}>
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <p className="text-white text-sm font-medium truncate">{s.name}</p>
                  {s.nif && <p className="text-gray-500 text-xs">NIF: {s.nif}</p>}
                </div>
                {s.balance > 0 && <span className="text-red-400 text-xs font-bold shrink-0 ml-2">{s.balance.toFixed(2)}€</span>}
              </div>
              {s.phone && <p className="text-gray-500 text-xs mt-0.5">{s.phone}</p>}
            </div>
          ))}
        </div>
        <div className="p-3 border-t border-gray-800 text-gray-500 text-xs">{filtered.length} fornecedores</div>
      </div>

      {/* Detalhe */}
      <div className="flex-1 overflow-y-auto">
        {!selected ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-600">
            <Building2 className="w-12 h-12 mb-3" />
            <p>Seleciona um fornecedor</p>
          </div>
        ) : (
          <div className="p-6 space-y-6 max-w-3xl">
            {/* Header */}
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-2xl font-bold text-white">{selected.name}</h2>
                {selected.nif && <p className="text-gray-400 text-sm">NIF: {selected.nif}</p>}
              </div>
              <div className="flex gap-2">
                <button onClick={() => openEdit(selected)} className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-2 rounded-lg text-sm flex items-center gap-1.5 transition-colors">
                  <Edit className="w-4 h-4" /> Editar
                </button>
                <button onClick={() => deleteSupplier(selected.id)} className="bg-red-900/40 hover:bg-red-900/60 text-red-400 px-3 py-2 rounded-lg text-sm flex items-center gap-1.5 transition-colors">
                  <Trash2 className="w-4 h-4" /> Desativar
                </button>
              </div>
            </div>

            {/* Balance Cards */}
            {balance && (
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-gray-800 rounded-xl p-4">
                  <p className="text-gray-400 text-xs mb-1">Total em Dívida</p>
                  <p className={`text-xl font-bold ${balance.total_debt > 0 ? 'text-red-400' : 'text-green-400'}`}>{balance.total_debt.toFixed(2)}€</p>
                </div>
                <div className="bg-gray-800 rounded-xl p-4">
                  <p className="text-gray-400 text-xs mb-1">Total Compras</p>
                  <p className="text-xl font-bold text-white">{balance.total_purchases.toFixed(2)}€</p>
                </div>
                <div className="bg-gray-800 rounded-xl p-4">
                  <p className="text-gray-400 text-xs mb-1">Vencidas</p>
                  <p className={`text-xl font-bold ${balance.overdue_amount > 0 ? 'text-red-400' : 'text-gray-400'}`}>{balance.overdue_amount.toFixed(2)}€</p>
                </div>
              </div>
            )}

            {/* Info */}
            <div className="bg-gray-800 rounded-xl p-4 grid grid-cols-2 gap-4">
              {selected.contact_name && <div className="flex items-center gap-2 text-sm"><Building2 className="w-4 h-4 text-gray-500 shrink-0" /><div><p className="text-gray-400 text-xs">Contacto</p><p className="text-white">{selected.contact_name}</p></div></div>}
              {selected.phone && <div className="flex items-center gap-2 text-sm"><Phone className="w-4 h-4 text-gray-500 shrink-0" /><div><p className="text-gray-400 text-xs">Telefone</p><p className="text-white">{selected.phone}</p></div></div>}
              {selected.email && <div className="flex items-center gap-2 text-sm"><Mail className="w-4 h-4 text-gray-500 shrink-0" /><div><p className="text-gray-400 text-xs">Email</p><p className="text-white">{selected.email}</p></div></div>}
              {(selected.address || selected.city) && <div className="flex items-center gap-2 text-sm"><MapPin className="w-4 h-4 text-gray-500 shrink-0" /><div><p className="text-gray-400 text-xs">Morada</p><p className="text-white">{[selected.address, selected.city, selected.postal_code].filter(Boolean).join(', ')}</p></div></div>}
              {selected.iban && <div className="flex items-center gap-2 text-sm col-span-2"><CreditCard className="w-4 h-4 text-gray-500 shrink-0" /><div><p className="text-gray-400 text-xs">IBAN</p><p className="text-white font-mono text-xs">{selected.iban}</p></div></div>}
              <div className="flex items-center gap-2 text-sm"><FileText className="w-4 h-4 text-gray-500 shrink-0" /><div><p className="text-gray-400 text-xs">Prazo Pagamento</p><p className="text-white">{selected.payment_terms} dias</p></div></div>
            </div>

            {/* Faturas recentes */}
            <div>
              <h3 className="text-white font-medium mb-3">Faturas Recentes</h3>
              {invoices.length === 0 ? <p className="text-gray-500 text-sm">Sem faturas registadas.</p> : (
                <div className="space-y-2">
                  {invoices.slice(0, 8).map(inv => (
                    <div
                      key={inv.id}
                      onClick={() => openInvoiceDetail(inv)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          openInvoiceDetail(inv);
                        }
                      }}
                      className="bg-gray-800 rounded-lg p-3 flex items-center justify-between cursor-pointer hover:bg-gray-700/80 transition-colors"
                      title="Abrir detalhe da fatura"
                    >
                      <div>
                        <p className="text-white text-sm font-medium">{inv.invoice_number || inv.invoice_no || "Fatura"}</p>
                        <p className="text-gray-500 text-xs">{formatDate(inv.invoice_date)} {inv.due_date && `· Vence: ${formatDate(inv.due_date)}`}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge status={inv.payment_status} />
                        <p className="text-white font-bold text-sm">{formatMoney(inv.total)}</p>
                        <ChevronRight className="w-4 h-4 text-gray-500" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {selected.notes && <div className="bg-gray-800 rounded-xl p-4"><p className="text-gray-400 text-xs mb-1">Notas</p><p className="text-gray-300 text-sm">{selected.notes}</p></div>}
          </div>
        )}
      </div>

      {/* Modal Detalhe da Fatura */}
      {showInvoiceDetail && selectedInvoice && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-gray-800">
            <div className="flex items-center justify-between p-6 border-b border-gray-800">
              <div>
                <h2 className="text-lg font-bold text-white">Detalhe da Fatura</h2>
                <p className="text-gray-500 text-sm">{selected?.name || "Fornecedor"}</p>
              </div>
              <button onClick={closeInvoiceDetail} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="bg-gray-800 rounded-xl p-4">
                  <p className="text-gray-400 text-xs mb-1">Número</p>
                  <p className="text-white font-semibold break-words">
                    {selectedInvoice.invoice_number || selectedInvoice.invoice_no || selectedInvoice.number || "-"}
                  </p>
                </div>
                <div className="bg-gray-800 rounded-xl p-4">
                  <p className="text-gray-400 text-xs mb-1">Data</p>
                  <p className="text-white font-semibold">{formatDate(selectedInvoice.invoice_date)}</p>
                </div>
                <div className="bg-gray-800 rounded-xl p-4">
                  <p className="text-gray-400 text-xs mb-1">Vencimento</p>
                  <p className="text-white font-semibold">{formatDate(selectedInvoice.due_date)}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="bg-gray-800 rounded-xl p-4">
                  <p className="text-gray-400 text-xs mb-1">Estado</p>
                  <Badge status={selectedInvoice.payment_status} />
                </div>
                <div className="bg-gray-800 rounded-xl p-4">
                  <p className="text-gray-400 text-xs mb-1">Total</p>
                  <p className="text-white font-bold">{formatMoney(selectedInvoice.total)}</p>
                </div>
                <div className="bg-gray-800 rounded-xl p-4">
                  <p className="text-gray-400 text-xs mb-1">Pago</p>
                  <p className="text-green-400 font-bold">
                    {formatMoney(selectedInvoice.paid_amount || selectedInvoice.amount_paid)}
                  </p>
                </div>
                <div className="bg-gray-800 rounded-xl p-4">
                  <p className="text-gray-400 text-xs mb-1">Em aberto</p>
                  <p className={invoiceOpenAmount(selectedInvoice) > 0 ? "text-red-400 font-bold" : "text-green-400 font-bold"}>
                    {formatMoney(invoiceOpenAmount(selectedInvoice))}
                  </p>
                </div>
              </div>

              <div className="bg-gray-800 rounded-xl p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-gray-400 text-xs mb-1">Fornecedor</p>
                  <p className="text-white">{selectedInvoice.supplier_name || selected?.name || "-"}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs mb-1">NIF fornecedor</p>
                  <p className="text-white">{selectedInvoice.supplier_nif || selected?.nif || "-"}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs mb-1">Método de pagamento</p>
                  <p className="text-white">{selectedInvoice.payment_method || "-"}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs mb-1">Referência</p>
                  <p className="text-white">{selectedInvoice.reference || selectedInvoice.document_reference || "-"}</p>
                </div>
              </div>

              <div className="bg-gray-800 rounded-xl p-4">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div>
                    <p className="text-white font-semibold">Produtos / Linhas da fatura</p>
                    <p className="text-gray-500 text-xs">
                      Detalhe dos artigos associados a esta fatura de fornecedor.
                    </p>
                  </div>
                  <span className="text-xs text-gray-400">
                    {invoiceItems(selectedInvoice).length} linha(s)
                  </span>
                </div>

                {invoiceItems(selectedInvoice).length === 0 ? (
                  <div className="rounded-lg border border-yellow-900/50 bg-yellow-950/30 p-3 text-sm text-yellow-300">
                    Esta fatura não tem linhas de produtos guardadas no detalhe recebido.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-700 text-gray-400">
                          <th className="text-left py-2 pr-3 font-medium">Produto</th>
                          <th className="text-right py-2 px-3 font-medium">Qtd</th>
                          <th className="text-right py-2 px-3 font-medium">Preço</th>
                          <th className="text-right py-2 px-3 font-medium">IVA</th>
                          <th className="text-right py-2 pl-3 font-medium">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoiceItems(selectedInvoice).map((item, idx) => (
                          <tr key={item.id || item.product_id || idx} className="border-b border-gray-800 align-top">
                            <td className="py-2 pr-3 text-white">
                              <div className="font-medium">{itemName(item)}</div>
                              {(item.reference || item.barcode || item.lot_number || item.expiry_date || item.batch_expiry_date) && (
                                <div className="text-xs text-gray-500 mt-0.5">
                                  {[
                                    item.reference ? `Ref: ${item.reference}` : "",
                                    item.barcode ? `Cód: ${item.barcode}` : "",
                                    item.lot_number ? `Lote: ${item.lot_number}` : "",
                                    (item.expiry_date || item.batch_expiry_date) ? `Val: ${item.expiry_date || item.batch_expiry_date}` : "",
                                  ].filter(Boolean).join(" · ")}
                                </div>
                              )}
                            </td>
                            <td className="py-2 px-3 text-right text-gray-300">{itemQty(item)}</td>
                            <td className="py-2 px-3 text-right text-gray-300">{formatMoney(itemUnitPrice(item))}</td>
                            <td className="py-2 px-3 text-right text-gray-300">
                              {itemVatRate(item) !== "" && itemVatRate(item) !== null && itemVatRate(item) !== undefined
                                ? `${itemVatRate(item)}%`
                                : "-"}
                            </td>
                            <td className="py-2 pl-3 text-right text-white font-semibold">{formatMoney(itemLineTotal(item))}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {(selectedInvoice.notes || selectedInvoice.description) && (
                <div className="bg-gray-800 rounded-xl p-4">
                  <p className="text-gray-400 text-xs mb-1">Notas</p>
                  <p className="text-gray-300 text-sm whitespace-pre-wrap">
                    {selectedInvoice.notes || selectedInvoice.description}
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 p-6 border-t border-gray-800">
              <button onClick={closeInvoiceDetail} className="bg-gray-700 hover:bg-gray-600 text-white px-5 py-2.5 rounded-xl text-sm">
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Formulário */}
      {showForm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-800">
              <h2 className="text-lg font-bold text-white">{editing ? "Editar Fornecedor" : "Novo Fornecedor"}</h2>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 grid grid-cols-2 gap-4">
              {[["Nome *", "name", "text", "col-span-2"], ["NIF", "nif", "text", ""], ["Contacto", "contact_name", "text", ""], ["Telefone", "phone", "tel", ""], ["Email", "email", "email", ""], ["Morada", "address", "text", "col-span-2"], ["Cidade", "city", "text", ""], ["Código Postal", "postal_code", "text", ""], ["Website", "website", "url", ""], ["IBAN", "iban", "text", "col-span-2"]].map(([label, field, type, cls]) => (
                <div key={field} className={cls}>
                  <label className="block text-gray-400 text-xs mb-1">{label}</label>
                  <input type={type} value={form[field] || ""} onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))} className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-600" />
                </div>
              ))}
              <div>
                <label className="block text-gray-400 text-xs mb-1">Prazo Pagamento (dias)</label>
                <select value={form.payment_terms} onChange={e => setForm(f => ({ ...f, payment_terms: parseInt(e.target.value) }))} className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-600">
                  {PAYMENT_TERMS.map(d => <option key={d} value={d}>{d} dias</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-gray-400 text-xs mb-1">Notas</label>
                <textarea value={form.notes || ""} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={3} className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-600 resize-none" />
              </div>
            </div>
            <div className="flex gap-3 p-6 border-t border-gray-800">
              <button onClick={() => setShowForm(false)} className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-2.5 rounded-xl text-sm">Cancelar</button>
              <button onClick={saveSupplier} disabled={saving || !form.name} className="flex-1 bg-green-600 hover:bg-green-500 disabled:opacity-50 text-white py-2.5 rounded-xl text-sm flex items-center justify-center gap-2">
                <Check className="w-4 h-4" />{saving ? "A guardar..." : "Guardar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
